// Pure rules for PATCH /api/settings. No `bun:sqlite` import on purpose: the
// admin form imports this to check values before sending, the server uses the
// same rules as the authority.
import {
  PLATFORM_CLOSED_KEY,
  PLATFORM_LOCK_PASSWORD_INPUT_KEY,
} from "@/lib/platform-lock-shared";
import { MONEY_SETTING_KEYS } from "@/lib/money/money-config";

type Rule =
  | { kind: "percent"; label: string }
  | { kind: "rate"; label: string }
  | { kind: "int"; label: string; min: number; max: number; unit: string }
  | { kind: "bool"; label: string }
  | { kind: "enum"; label: string; values: readonly string[] }
  | { kind: "text"; label: string; required?: boolean; max: number }
  | { kind: "country"; label: string }
  // Validated elsewhere (cross-key checks against stored values).
  | { kind: "external" };

const RULES: Record<string, Rule> = {
  platform_fee_percent: { kind: "percent", label: "Provizija platforme" },
  cancel_fee_percent: { kind: "percent", label: "Naknada za kasno otkazivanje" },
  cancel_deadline_hours: {
    kind: "int",
    label: "Rok za otkazivanje",
    min: 0,
    max: 168,
    unit: "sati",
  },
  [MONEY_SETTING_KEYS.vatRate]: { kind: "rate", label: "PDV stopa za seanse" },
  [MONEY_SETTING_KEYS.recognizedCosts]: { kind: "percent", label: "Priznati troškovi" },
  [MONEY_SETTING_KEYS.incomeTax]: { kind: "percent", label: "Porez na dohodak" },
  [MONEY_SETTING_KEYS.pension]: { kind: "percent", label: "Doprinos za PIO" },
  [MONEY_SETTING_KEYS.health]: { kind: "percent", label: "Doprinos za zdravstvo" },
  platform_name: { kind: "text", label: "Naziv platforme", required: true, max: 100 },
  [PLATFORM_CLOSED_KEY]: { kind: "bool", label: "Zatvori platformu" },
  [PLATFORM_LOCK_PASSWORD_INPUT_KEY]: { kind: "text", label: "Platform lock šifra", max: 200 },
  bank_name: { kind: "text", label: "Naziv banke", max: 200 },
  bank_account_number: { kind: "text", label: "Broj računa", max: 100 },
  bank_recipient_name: { kind: "text", label: "Primalac", max: 200 },
  bank_reference_prefix: { kind: "text", label: "Prefiks reference", max: 50 },
  business_pricing_force_default: {
    kind: "bool",
    label: "Poslovni računi plaćaju cijene za BiH",
  },
  business_pricing_default_country: { kind: "country", label: "Zemlja za poslovne račune" },
  service_price_min: { kind: "external" },
  service_price_max: { kind: "external" },
  payout_request_min_amount: { kind: "external" },
};

/** Keys PATCH /api/settings may write. Everything else has its own endpoint or is internal. */
export const WRITABLE_SETTING_KEYS: ReadonlySet<string> = new Set(Object.keys(RULES));

function rejectedKeyMessage(key: string): string {
  if (key === "footer_config") {
    return "Podnožje se uređuje u sekciji Podnožje, ne kroz opšte postavke.";
  }
  if (key.startsWith("email_tpl_")) {
    return "Email šabloni se uređuju u sekciji Email šabloni, ne kroz opšte postavke.";
  }
  if (key === "featured_therapists") {
    return "Istaknuti terapeuti se biraju u sekciji Istaknuti terapeuti, ne kroz opšte postavke.";
  }
  if (key.startsWith("subscription_")) {
    return "Postavke pretplate se uređuju u sekciji Pretplate, ne kroz opšte postavke.";
  }
  return `Postavka "${key}" se ne može mijenjati kroz opšte postavke.`;
}

function parseStrictNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function validateValue(rule: Rule, raw: string): string | null {
  switch (rule.kind) {
    case "external":
      return null;
    case "percent": {
      const n = parseStrictNumber(raw);
      if (n === null || n < 0 || n > 100) {
        return `${rule.label}: unesite broj od 0 do 100 (procenat, npr. 20).`;
      }
      return null;
    }
    case "rate": {
      // Stored as a fraction (0.17 = 17%), the way getMoneyConfig reads it.
      const n = parseStrictNumber(raw);
      if (n === null || n < 0 || n >= 1) {
        return `${rule.label}: unesite decimalni broj između 0 i 1, npr. 0.17 za 17%.`;
      }
      return null;
    }
    case "int": {
      const n = parseStrictNumber(raw);
      if (n === null || !Number.isInteger(n) || n < rule.min || n > rule.max) {
        return `${rule.label}: unesite cijeli broj od ${rule.min} do ${rule.max} (${rule.unit}).`;
      }
      return null;
    }
    case "bool":
      return raw === "true" || raw === "false"
        ? null
        : `${rule.label}: vrijednost mora biti uključeno ili isključeno.`;
    case "enum":
      return rule.values.includes(raw)
        ? null
        : `${rule.label}: dozvoljene vrijednosti su ${rule.values.join(", ")}.`;
    case "text": {
      const trimmed = raw.trim();
      if (rule.required && trimmed === "") return `${rule.label}: polje je obavezno.`;
      if (trimmed.length > rule.max) {
        return `${rule.label}: najviše ${rule.max} znakova.`;
      }
      return null;
    }
    case "country":
      return raw.trim() === "" || /^[A-Za-z]{2}$/.test(raw.trim())
        ? null
        : `${rule.label}: unesite dvoslovni kod zemlje, npr. BA.`;
  }
}

/**
 * Per-key check of a settings patch: rejects keys this endpoint must not write
 * and values outside their range. Returns a Bosnian message, or null when every
 * key is fine. Cross-key checks (price range vs stored values) live on the server.
 */
export function validateSettingsPatch(body: Record<string, string>): string | null {
  for (const [key, value] of Object.entries(body)) {
    const rule = RULES[key];
    if (!rule) return rejectedKeyMessage(key);
    const error = validateValue(rule, String(value));
    if (error) return error;
  }
  return null;
}
